/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Ad Studio prompting guide", description = "How to prompt Ad Studio — what to put in a prompt, how to generate a first batch, how to iterate without drift, and templates for motion, UGC, and localized ads.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Ad Studio prompting guide",
        description: "How to prompt Ad Studio — what to put in a prompt, how to generate a first batch, how to iterate without drift, and templates for motion, UGC, and localized ads.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Two habits that matter most",
          "depth": 0,
          "id": "two-habits-that-matter-most"
        }, {
          "value": "What goes in a prompt",
          "depth": 0,
          "id": "what-goes-in-a-prompt"
        }, {
          "value": "Attach reference images",
          "depth": 1,
          "id": "attach-reference-images"
        }, {
          "value": "Select a style",
          "depth": 1,
          "id": "select-a-style"
        }, {
          "value": "Reuse assets from your workspace",
          "depth": 1,
          "id": "reuse-assets-from-your-workspace"
        }, {
          "value": "Give Ad Studio your source material",
          "depth": 1,
          "id": "give-ad-studio-your-source-material"
        }, {
          "value": "Ask for a batch, not one ad",
          "depth": 1,
          "id": "ask-for-a-batch-not-one-ad"
        }, {
          "value": "Describe the visual style in a few words",
          "depth": 1,
          "id": "describe-the-visual-style-in-a-few-words"
        }, {
          "value": "Ask for questions before generating",
          "depth": 1,
          "id": "ask-for-questions-before-generating"
        }, {
          "value": "Create static ads",
          "depth": 0,
          "id": "create-static-ads"
        }, {
          "value": "Start from scratch",
          "depth": 1,
          "id": "start-from-scratch"
        }, {
          "value": "Start from a brief",
          "depth": 1,
          "id": "start-from-a-brief"
        }, {
          "value": "Start from a web page",
          "depth": 1,
          "id": "start-from-a-web-page"
        }, {
          "value": "Start from a reference ad",
          "depth": 1,
          "id": "start-from-a-reference-ad"
        }, {
          "value": "Start from your existing assets",
          "depth": 1,
          "id": "start-from-your-existing-assets"
        }, {
          "value": "Iterate on results in the chat",
          "depth": 0,
          "id": "iterate-on-results-in-the-chat"
        }, {
          "value": "Keep each prompt focused",
          "depth": 1,
          "id": "keep-each-prompt-focused"
        }, {
          "value": "Say what must stay the same",
          "depth": 1,
          "id": "say-what-must-stay-the-same"
        }, {
          "value": "Generate more concepts from a direction",
          "depth": 1,
          "id": "generate-more-concepts-from-a-direction"
        }, {
          "value": "Edit a batch of ads in one prompt",
          "depth": 1,
          "id": "edit-a-batch-of-ads-in-one-prompt"
        }, {
          "value": "Create motion ads",
          "depth": 0,
          "id": "create-motion-ads"
        }, {
          "value": "Animate an approved static",
          "depth": 1,
          "id": "animate-an-approved-static"
        }, {
          "value": "Write a motion prompt",
          "depth": 1,
          "id": "write-a-motion-prompt"
        }, {
          "value": "Structure complex motion prompts",
          "depth": 1,
          "id": "structure-complex-motion-prompts"
        }, {
          "value": "Ask for a custom component",
          "depth": 1,
          "id": "ask-for-a-custom-component"
        }, {
          "value": "Prompt for specialized formats",
          "depth": 0,
          "id": "prompt-for-specialized-formats"
        }, {
          "value": "UGC-style video",
          "depth": 1,
          "id": "ugc-style-video"
        }, {
          "value": "Slideshows and GIFs",
          "depth": 1,
          "id": "slideshows-and-gifs"
        }, {
          "value": "Memes",
          "depth": 1,
          "id": "memes"
        }, {
          "value": "Motion graphics",
          "depth": 1,
          "id": "motion-graphics"
        }, {
          "value": "Resize an ad made outside Ad Studio",
          "depth": 1,
          "id": "resize-an-ad-made-outside-ad-studio"
        }, {
          "value": "Turn a long video into multiple ads",
          "depth": 0,
          "id": "turn-a-long-video-into-multiple-ads"
        }, {
          "value": "Customize the output",
          "depth": 1,
          "id": "customize-the-output"
        }, {
          "value": "Translate or localize your ads",
          "depth": 0,
          "id": "translate-or-localize-your-ads"
        }, {
          "value": "Review the translations",
          "depth": 1,
          "id": "review-the-translations"
        }, {
          "value": "Common questions",
          "depth": 0,
          "id": "common-questions"
        }, {
          "value": "What's next",
          "depth": 0,
          "id": "whats-next"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ol: "ol",
      li: "li",
      ul: "ul",
      h3: "h3",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, creative teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.a, {
                    href: "/ad-studio/overview#get-started",
                    children: "Ad Studio setup complete"
                  })
                }), _jsxs("li", {
                  children: [_jsx(_components.a, {
                    href: "/context-hub/brands",
                    children: "Brand context configured"
                  }), " in Context Hub"]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "This guide helps you decide what goes in a prompt, generate a batch of concepts, then iterate on the ones worth keeping. Copy any template and replace the [BRACKETED] placeholders with your campaign details."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "two-habits-that-matter-most",
        children: "Two habits that matter most"
      }), "\n", _jsx(_components.p, {
        children: "As you work, two habits do more than any single prompt to speed up a session and improve the results."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Iterate in the chat instead of starting over."
            }), " Select several ads and they become the context for your next message, so one prompt applies to all of them and the edits run in parallel: \"change the CTA on all of these,\" \"apply this badge to the batch.\" Be surgical — \"hold everything else, just change the headline\" beats vague feedback. It works for motion too: select statics and animate them in one request."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Run several chats at once."
            }), " You don't have to wait for one chat to finish before starting the next. Kick off a few in parallel, one per campaign, format, or idea, and check back as they land. Each chat works best as a single, focused ask."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "what-goes-in-a-prompt",
        children: "What goes in a prompt"
      }), "\n", _jsxs(_components.p, {
        children: ["Ad Studio already has your brand context and your ", _jsx(_components.a, {
          href: "/context-hub/assets",
          children: "assets"
        }), ", so start with the three things it can't infer from your workspace:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Goal"
          }), " — The business outcome, such as app installs or brand awareness."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience"
          }), " — The persona, funnel stage, or segment."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Offer"
          }), " — The product, SKU, or value prop being promoted."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The rest of this section covers what to add on top: references, library assets, source material, how many ads you want, and the visual style. A sentence or two is enough for a small iteration."
      }), "\n", _jsx(_components.h3, {
        id: "attach-reference-images",
        children: "Attach reference images"
      }), "\n", _jsx(_components.p, {
        children: "Attach a reference image when you want Ad Studio to match a specific visual style. The closer the reference is to what you want, the closer the generated ads will be. A reference can be one of your own ads, a competitor ad, or any photo with a look you want to match."
      }), "\n", _jsx(_components.h3, {
        id: "select-a-style",
        children: "Select a style"
      }), "\n", _jsx(_components.p, {
        children: "Select a style before you generate. Ad Studio uses it as the visual direction for every image in that request, so a style keeps a batch — or a whole campaign — consistent without restating the look each time. Styles are tied to a brand profile, so changing the brand profile clears the style selection."
      }), "\n", _jsx(_components.h3, {
        id: "reuse-assets-from-your-workspace",
        children: "Reuse assets from your workspace"
      }), "\n", _jsx(_components.p, {
        children: "Ad Studio can reuse creative you already have:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Assets you've uploaded in ", _jsx(_components.a, {
            href: "/context-hub/assets",
            children: "Assets"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Products from your ", _jsx(_components.a, {
            href: "/context-hub/assets#connect-a-product-catalog",
            children: "product catalog"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Ads synced from your connected ad platforms"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Name the asset in your prompt, either exactly or loosely, such as \"the blue sweater.\" Then say whether to reuse it as-is or treat it as inspiration. Without that instruction an image you meant to preserve can come back regenerated."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["For pixel-exact type and spacing, generate against a template. A template is a ", _jsx(_components.strong, {
            children: "Component"
          }), " asset that holds your exact layout, fonts, and spacing, so the agent swaps in new copy and imagery without redrawing the design. Set one up in ", _jsx(_components.a, {
            href: "/context-hub/assets#upload-figma-templates",
            children: "Assets"
          }), ", or ask your Hightouch team to help."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "give-ad-studio-your-source-material",
        children: "Give Ad Studio your source material"
      }), "\n", _jsx(_components.p, {
        children: "Rather than describing your product from scratch, attach what you already wrote:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "A campaign brief"
        }), "\n", _jsx(_components.li, {
          children: "A landing page URL"
        }), "\n", _jsx(_components.li, {
          children: "Product screenshots"
        }), "\n", _jsx(_components.li, {
          children: "Reference ads"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ad Studio pulls headlines, body copy, and value props from this material instead of inventing them."
      }), "\n", _jsx(_components.h3, {
        id: "ask-for-a-batch-not-one-ad",
        children: "Ask for a batch, not one ad"
      }), "\n", _jsxs(_components.p, {
        children: ["Always include a number. Ask for ", _jsx(_components.strong, {
          children: "7–10 ads per request"
        }), " as your default, and keep any single request under 15."]
      }), "\n", _jsx(_components.p, {
        children: "Asking for a batch changes what Ad Studio does, not just how much it returns: it picks a differentiation axis — product, message, persona, or concept — and varies the set along it. Ask for one ad and you get one ad, with nothing to compare it against."
      }), "\n", _jsxs(_components.p, {
        children: ["Ten take barely longer than two, since Ad Studio generates them in parallel. A reliable rhythm: ask for 10, keep the best, then ", _jsx(_components.a, {
          href: "#generate-more-concepts-from-a-direction",
          children: "ask for 10 more in that direction"
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "\"Create 10 variants\" tends to return copy-only changes. If you want the imagery to vary too, say so explicitly."
        })
      }), "\n", _jsx(_components.h3, {
        id: "describe-the-visual-style-in-a-few-words",
        children: "Describe the visual style in a few words"
      }), "\n", _jsx(_components.p, {
        children: "A few style words steer the output more than a long description of the subject does. Useful vocabulary:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Premium"
        }), "\n", _jsx(_components.li, {
          children: "Editorial"
        }), "\n", _jsx(_components.li, {
          children: "Minimal"
        }), "\n", _jsx(_components.li, {
          children: "High fashion"
        }), "\n", _jsx(_components.li, {
          children: "Bold flat illustration"
        }), "\n", _jsx(_components.li, {
          children: "Film grain"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Stack a few into a directive. For example: \"These ads must feel premium and elevated. Editorial style. High fashion. Slight film grain.\""
      }), "\n", _jsx(_components.h3, {
        id: "ask-for-questions-before-generating",
        children: "Ask for questions before generating"
      }), "\n", _jsx(_components.p, {
        children: "When your brief is incomplete, ask Ad Studio to gather what it's missing before it generates anything. This turns one guess into a short exchange, which matters most when the campaign is complicated:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["Ask me any questions before you begin that will help you get the best answer. If you need more product information, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "case"
          }), " study info, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "or"
          }), " product screens, ask me ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " I can provide them.\n"]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-static-ads",
        children: "Create static ads"
      }), "\n", _jsx(_components.p, {
        children: "Statics are single-image ads. There are five ways to start, depending on what you already have."
      }), "\n", _jsx(_components.h3, {
        id: "start-from-scratch",
        children: "Start from scratch"
      }), "\n", _jsx(_components.p, {
        children: "Use this when you have a campaign in mind but no materials yet:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Create ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), " distinct ad concepts for ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[CAMPAIGN NAME / TYPE]"
          }), " campaign for ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[CHANNEL]"
          }), ". The audience is ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[AUDIENCE]"
          }), " in the ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[VERTICAL]"
          }), " vertical. Vary the hook, visual ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "direction"
          }), ", and format across concepts.\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "To narrow the output, attach a reference image, paste a landing page URL, or ask for specific sizes."
      }), "\n", _jsx(_components.h3, {
        id: "start-from-a-brief",
        children: "Start from a brief"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.a, {
          href: "/ad-studio/ads#upload-a-brief",
          children: _jsx(_components.strong, {
            children: "Upload a brief"
          })
        }), " and attach your file, and Ad Studio writes the prompt for you. Add instructions on top to control the count or point at the part of the brief that matters most:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Based ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "on"
          }), " the brief attached, generate ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "new"
          }), " variations ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " my [SEASON/CAMPAIGN] campaign. The goal ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "is"
          }), " editorial variations ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), " a [SEASON] angle, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "while"
          }), " adhering ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " my brand guidelines.\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Key"
          }), " points:\n- Focus ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "on"
          }), " [SEASON] seasonality ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " the primary angle. Avoid other seasonal themes.\n- Generate no more than ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), " variations per creative.\n- Base all iterations ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "on"
          }), " the insights, best practices, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          }), " learnings ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " the document.\n- Adapt messaging, visuals, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          }), " hooks accordingly, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "while"
          }), " keeping them performance-driven.\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "start-from-a-web-page",
        children: "Start from a web page"
      }), "\n", _jsx(_components.p, {
        children: "Use this when your messaging already lives on a landing page:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Generate ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), " ad concepts targeted ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[AUDIENCE]"
          }), ". Pull copy and value props ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "from"
          }), " this webpage: [URL]\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "start-from-a-reference-ad",
        children: "Start from a reference ad"
      }), "\n", _jsx(_components.p, {
        children: "Two prompts, depending on how far you want to travel from the reference. To stay close, which gives you a consistent templated set:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["Generate ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), " ", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["ads ", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "using"
            }), " the style of the attached ", _jsx(_components.span, {
              className: "hljs-title",
              children: "image"
            }), _jsx(_components.span, {
              className: "hljs-params",
              children: "(s)"
            }), ".\n"]
          })]
        })
      }), "\n", _jsx(_components.p, {
        children: "To explore a wider range, which gives you options to test against each other:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["Use the attached ad as a starting point ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " produce ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), " variants. Hold the core message, but vary headlines, hooks, visual crops, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " CTA framing so we have a meaningful test set.\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "start-from-your-existing-assets",
        children: "Start from your existing assets"
      }), "\n", _jsx(_components.p, {
        children: "Use this when you want new ads built from imagery you already own:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Create ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), " ads ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "this"
          }), " style but ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "using"
          }), " existing imagery ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " our asset library. Promote our [PRODUCT] ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "using"
          }), " different messaging angles.\n"]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "iterate-on-results-in-the-chat",
        children: "Iterate on results in the chat"
      }), "\n", _jsx(_components.p, {
        children: "Most of the work happens after the first batch. These habits separate a fast session from a frustrating one."
      }), "\n", _jsx(_components.h3, {
        id: "keep-each-prompt-focused",
        children: "Keep each prompt focused"
      }), "\n", _jsx(_components.p, {
        children: "Give each prompt one job. Instead of one request that does everything:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Make these ads brighter, change the CTA, use ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " different audience, add our spring colors, and ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "resize"
          }), " for Instagram.\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Send three:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-objectivec",
          children: ["Keep everything ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "else"
          }), " the same. Change only the ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CTA"
          }), ".\n"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "Generate Instagram versions.\n"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Try ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " brighter ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "color"
          }), " palette.\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Each request produces a more predictable result, and when a round misses you know which instruction caused it."
      }), "\n", _jsx(_components.h3, {
        id: "say-what-must-stay-the-same",
        children: "Say what must stay the same"
      }), "\n", _jsx(_components.p, {
        children: "Whenever you iterate, name what must not change. Ad Studio regenerates the ad on each round, so anything you don't protect can drift from the version you approved. Useful instructions:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Keep the layout."
        }), "\n", _jsx(_components.li, {
          children: "Keep the headline."
        }), "\n", _jsx(_components.li, {
          children: "Don't regenerate the product image."
        }), "\n", _jsx(_components.li, {
          children: "Preserve the typography."
        }), "\n", _jsx(_components.li, {
          children: "Use the same visual style."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Restate these on every round, not just the first. \"Hold everything else, just change the headline\" is specific enough to act on; \"make these better\" isn't."
      }), "\n", _jsx(_components.h3, {
        id: "generate-more-concepts-from-a-direction",
        children: "Generate more concepts from a direction"
      }), "\n", _jsx(_components.p, {
        children: "Start by asking for 7–10 concepts. Once you find a direction you like, ask for another batch based on that concept instead of starting over:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["Use ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "concept"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "4"
          }), " as the direction ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " generate ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), " more, varying the headline ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " visual crop.\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "edit-a-batch-of-ads-in-one-prompt",
        children: "Edit a batch of ads in one prompt"
      }), "\n", _jsx(_components.p, {
        children: "Select one or more ads in a chat before you prompt. Your selection becomes the context for the next request, so one prompt applies to every ad you selected and the edits run in parallel. For example:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Change the CTA on all selected ads."
        }), "\n", _jsx(_components.li, {
          children: "Apply a badge to the whole batch."
        }), "\n", _jsx(_components.li, {
          children: "Animate every selected static."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-motion-ads",
        children: "Create motion ads"
      }), "\n", _jsx(_components.p, {
        children: "Motion ads are short videos generated from an existing static ad. Your approved layout, typography, and branding stay the same. Ad Studio adds animation around those elements, so your prompt only needs to describe how the ad should move."
      }), "\n", _jsx(_components.h3, {
        id: "animate-an-approved-static",
        children: "Animate an approved static"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Create a motion ad ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "static"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " adding subtle motion to the entire ad.\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "To leave the imagery untouched:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Create a motion ad ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "static"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " only animating the text, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "not"
          }), " the imagery.\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "write-a-motion-prompt",
        children: "Write a motion prompt"
      }), "\n", _jsx(_components.p, {
        children: "Include four things every time:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Video length"
          }), " — A duration in seconds, not \"short.\""]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Elements that must not change"
          }), " — Typography, logo, layout, and product."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Whether the animation loops"
          }), " — Say so if you need a seamless loop."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The motion to apply"
          }), " — One idea, described in plain words."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To build text in piece by piece:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["Animate ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "static"
          }), " ad as a build-in sequence, roughly ", _jsx(_components.span, {
            className: "hljs-number",
            children: "5"
          }), " seconds. Each element should fade in sequentially from top to bottom. The headline should fade in line by line. Do ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "not"
          }), " regenerate, warp, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "or"
          }), " alter any typography, logos, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "or"
          }), " the photo — every element must land exactly in its ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "final"
          }), " position matching the original image.\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "To add subtle motion to the product shot, describe the movement in plain words based on what's in the image:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["Animate ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "static"
          }), " ad. Keep all text, logos, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " layout locked in place — ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "do"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "not"
          }), " regenerate ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "or"
          }), " warp any typography. Subtle, warm, premium product animation, ", _jsx(_components.span, {
            className: "hljs-number",
            children: "5"
          }), "–", _jsx(_components.span, {
            className: "hljs-number",
            children: "6"
          }), " seconds, seamless loop:\n[DESCRIBE THE MOTION YOU WANT, BASED ON THE IMAGERY]\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "structure-complex-motion-prompts",
        children: "Structure complex motion prompts"
      }), "\n", _jsx(_components.p, {
        children: "When you need precise control, use labeled sections and list exactly what may move. Everything you don't list stays static:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["── INSTRUCTIONS ───\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Create"
          }), " a ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "set"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "of"
          }), " resized video ads ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " this static.\n", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ":", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), "\n", _jsx(_components.span, {
            className: "hljs-number",
            children: "9"
          }), ":", _jsx(_components.span, {
            className: "hljs-number",
            children: "16"
          }), "\n", _jsx(_components.span, {
            className: "hljs-number",
            children: "16"
          }), ":", _jsx(_components.span, {
            className: "hljs-number",
            children: "9"
          }), "\n\n── LAYOUT ───\nKeep the overall hierarchy the same — logo, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "copy"
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " product — adjusting font sizes ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " line breaks ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "where"
          }), " needed. Make sure spacing ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "between"
          }), " elements looks tasteful ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " well designed, ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "not"
          }), " too cramped.\n\n── PERSISTENT ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "STATIC"
          }), " ELEMENTS ───\nLogo\nHeadline\nSubhead\nLegal ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "copy"
          }), "\n\n── MOTION ───\nCRITICAL: ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ONLY"
          }), " ANIMATE THE ITEMS LISTED BELOW ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " THIS SECTION. EVERYTHING ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ELSE"
          }), " SHOULD BE STATIC.\n[ELEMENT]: [", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "DESCRIBE"
          }), " ITS ANIMATION]\n\n── VIDEO LENGTH ───\nVideo should be ", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), " seconds long.\n\n── CRITICAL NOTES ───\n", _jsx(_components.span, {
            className: "hljs-number",
            children: "1."
          }), " [ANYTHING THAT MUST ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " CHANGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OR"
          }), " ANIMATE]\n\nMake sure ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " save the output ads so I can ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "view"
          }), " them.\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "ask-for-a-custom-component",
        children: "Ask for a custom component"
      }), "\n", _jsxs(_components.p, {
        children: ["Ask for a ", _jsx(_components.strong, {
          children: "custom component"
        }), " when you need end cards, transitions, or text animations that don't require AI-generated imagery. Ad Studio writes code for the animation instead of generating new pixels, which keeps type and edges crisp. It sometimes chooses this on its own, but naming it in your prompt makes it consistent."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "prompt-for-specialized-formats",
        children: "Prompt for specialized formats"
      }), "\n", _jsxs(_components.p, {
        children: ["Where you see ", _jsx(_components.code, {
          children: "{{STATIC_ASSET}}"
        }), " below, substitute the static ad you've selected or attached."]
      }), "\n", _jsx(_components.h3, {
        id: "ugc-style-video",
        children: "UGC-style video"
      }), "\n", _jsx(_components.p, {
        children: "Plan the ad before you prompt: write the script, then the creative direction, then a scene map. Paste the full plan in, then the template:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Create a vertical ", _jsx(_components.span, {
            className: "hljs-number",
            children: "9"
          }), ":", _jsx(_components.span, {
            className: "hljs-number",
            children: "16"
          }), " selfie-style video that feels ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "like"
          }), " native TikTok UGC.\nThe camera ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "is"
          }), " handheld, front-facing, slightly shaky, filmed ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " natural indoor lighting.\nA real human creator ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "is"
          }), " holding ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "or"
          }), " referencing {{STATIC_ASSET}} naturally ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "while"
          }), " speaking directly ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " camera.\n\n", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "Framing:"
          }), " chest-up talking head, casual posture, imperfect composition.\n", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "Motion:"
          }), " subtle hand movements, slight camera drift, natural head movement.\n", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "Style:"
          }), " unpolished, authentic, iPhone video look.\n", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "Lighting:"
          }), " soft, uneven, realistic indoor lighting (no studio feel).\n\nAdd subtle ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "auto"
          }), "-captions synced ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " speech.\nNo cinematic effects, no transitions, no polish.\nFeels ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "like"
          }), " a real creator explaining something ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " a ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "friend"
          }), ".\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "slideshows-and-gifs",
        children: "Slideshows and GIFs"
      }), "\n", _jsx(_components.p, {
        children: "Build these from statics you already like, and ask for background music if you want it."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Create a vertical slideshow-style video ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "using"
          }), " multiple crops ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "or"
          }), " variations ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "of"
          }), " {{STATIC_ASSET}}.\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Each"
          }), " frame appears ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " a still image ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), " subtle motion.\n\n", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "Transitions:"
          }), " simple fades, vertical slides, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "or"
          }), " quick cuts.\n", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "Timing:"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "each"
          }), " slide lasts ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), "–", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), " seconds.\nOverlay ", _jsx(_components.span, {
            className: "hljs-type",
            children: "short"
          }), " caption ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "text"
          }), " aligned center ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "or"
          }), " bottom.\n\nNo complex animation.\nFeels ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "like"
          }), " a ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "static"
          }), " carousel converted ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "into"
          }), " a lightweight video ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " ads.\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "memes",
        children: "Memes"
      }), "\n", _jsxs(_components.p, {
        children: ["Memes need a style to work from. Upload meme reference ads as ", _jsx(_components.a, {
          href: "/context-hub/assets",
          children: "assets"
        }), ", select them, and generate a ", _jsx(_components.a, {
          href: "/context-hub/creative#save-visual-styles",
          children: "style"
        }), " from the selection. Then generate against that style."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Create a vertical meme-style video that follows native TikTok trend grammar.\nUse {{STATIC_ASSET}} ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " the punchline ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "or"
          }), " visual reveal.\n\n", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "Structure:"
          }), " setup → beat → payoff.\nAdd exaggerated zooms, abrupt cuts, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "on"
          }), "-screen caption humor.\nMotion should feel intentionally rough ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          }), " fast.\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Text"
          }), " overlays mimic TikTok meme captions.\nFeels culturally current, slightly ironic, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          }), " low-effort ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " design.\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "motion-graphics",
        children: "Motion graphics"
      }), "\n", _jsxs(_components.p, {
        children: ["Motion graphics are illustrated animation with no photography. To animate an existing static instead, see ", _jsx(_components.a, {
          href: "#create-motion-ads",
          children: "Create motion ads"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Create a ", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), "D illustrated motion graphic video inspired ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " flat, vector-style animation.\nTranslate {{STATIC_ASSET}} ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "into"
          }), " simplified illustrated elements ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " needed.\n\n", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "Motion:"
          }), " smooth easing, clean transitions, floating icons, sliding panels.\n", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "Style:"
          }), " minimal, modern, friendly.\nColor palette: cohesive ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          }), " brand-safe.\n\nNo realism ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "or"
          }), " photographic textures.\nFeels ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "like"
          }), " a polished animated explainer adapted ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " social feeds.\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "resize-an-ad-made-outside-ad-studio",
        children: "Resize an ad made outside Ad Studio"
      }), "\n", _jsx(_components.p, {
        children: "Upload the ad, then ask for the new ratio and nothing else:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["Use the attached ad ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " create a [ASPECT RATIO] size. Keep the composition as close to the original as possible. All elements should stay the same — fonts, colors, image, logo, etc. The ONLY change I want is the ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " aspect ratio. Protect the original composition ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " elements.\n"]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "turn-a-long-video-into-multiple-ads",
        children: "Turn a long video into multiple ads"
      }), "\n", _jsx(_components.p, {
        children: "Use this workflow when you already have a long video — such as a brand film, product demo, webinar, or customer story — and want to create multiple short ads from it."
      }), "\n", _jsx(_components.p, {
        children: "Attach the video, then prompt the agent:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "Take"
          }), " this ", _jsx(_components.span, {
            className: "hljs-type",
            children: "long"
          }), "-form video ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "and"
          }), " split it ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "into"
          }), " standalone ", _jsx(_components.span, {
            className: "hljs-type",
            children: "short"
          }), " ads.\n\nCut only ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "on"
          }), " natural scene breaks so ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "each"
          }), " clip tells one complete story ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), " a ", _jsx(_components.span, {
            className: "hljs-type",
            children: "single"
          }), " clear reason ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " believe.\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "End"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "each"
          }), " clip ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), " an ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "end"
          }), " card that includes:\n- our logo\n- a headline that matches that clip", _jsx(_components.span, {
            className: "hljs-comment",
            children: "'s message"
          }), "\n- a ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "call"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " action\n\nKeep the ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "end"
          }), " card visually consistent ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), " the rest ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "of"
          }), " the video.\n\nSave ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "each"
          }), " clip ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " an editable video ad.\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The agent creates a separate editable ad for each clip. You can review them individually, continue prompting, generate additional aspect ratios, or launch them like any other video ad."
      }), "\n", _jsxs(_components.p, {
        children: ["If a clip starts or ends in the wrong place, adjust it in the ", _jsx(_components.a, {
          href: "/ad-studio/refine#edit-with-the-visual-editor",
          children: "visual editor"
        }), " instead of re-prompting."]
      }), "\n", _jsx(_components.h3, {
        id: "customize-the-output",
        children: "Customize the output"
      }), "\n", _jsx(_components.p, {
        children: "Adjust the prompt to match your source video."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Clip length"
          }), " — Specify a duration if you need one, such as 6–10 seconds or under 15 seconds."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Message"
          }), " — Ask each clip to focus on one product benefit, customer story, or proof point."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "End card"
          }), " — Change the headline, CTA, or branding requirements if needed."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Output"
          }), " — Ask for additional aspect ratios or captions during the same conversation."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["Split this video ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "into"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "6"
          }), "–", _jsx(_components.span, {
            className: "hljs-number",
            children: "10"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "second"
          }), " ads.\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Each"
          }), " ad should focus ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "on"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "one"
          }), " customer benefit.\n\nGenerate ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ":", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ", ", _jsx(_components.span, {
            className: "hljs-number",
            children: "4"
          }), ":", _jsx(_components.span, {
            className: "hljs-number",
            children: "5"
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "and"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "9"
          }), ":", _jsx(_components.span, {
            className: "hljs-number",
            children: "16"
          }), " versions ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "every"
          }), " clip.\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "Add"
          }), " burned", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " captions.\n"]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "translate-or-localize-your-ads",
        children: "Translate or localize your ads"
      }), "\n", _jsx(_components.p, {
        children: "Request an adaptation or localization, meaning copy rewritten for the local culture, rather than a word-for-word translation. Specify the language, locale, and regional variant, and always ask for English back-translations so reviewers can verify the copy:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-less",
          children: [_jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "OBJECTIVE"
          }), "\n", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Create"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[LANGUAGE / LOCALE]"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "ads"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "my"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[CAMPAIGN]"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "campaign"
          }), ". ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "I"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "want"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "mix"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "lifestyle"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "photography"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "product"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "shots"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "and"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "typography-only"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "ads"
          }), ".\n\n", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "INSTRUCTIONS"
          }), "\n", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ". ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "For"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "every"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "ad"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "generate"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[LOCALE]"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "version"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "that"
          }), ":\n   ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Adapts"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "messaging"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[CAMPAIGN THEME]"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "themes"
          }), "\n   ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Uses"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "natural"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[REGIONAL VARIANT]"
          }), " — ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "not"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[OTHER VARIANT TO AVOID]"
          }), "\n   ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Maintains"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "original"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "ad"
          }), "'", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "s"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "structure"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "tone"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "and"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "call-to-action"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "intent"
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-number",
            children: "2"
          }), ". ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "For"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "each"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "piece"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "copy"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "you"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "write"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "immediately"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "provide"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "English"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "translation"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "in"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "brackets"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "so"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "reviewer"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "can"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "verify"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "accuracy"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "and"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "intent"
          }), ". ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Include"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "English"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "translation"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "headline"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "and"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "all"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "other"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "on-ad"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "copy"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "in"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "ad"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "brief"
          }), ".\n\n", _jsx(_components.span, {
            className: "hljs-number",
            children: "3"
          }), ". ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Add"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "short"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "note"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "explaining"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "what"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "local"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "seasonal"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "element"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "you"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "incorporated"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "and"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "why"
          }), ".\n\n", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "TONE"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "&"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "STYLE"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "GUIDELINES"
          }), "\n", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Warm"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "approachable"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "and"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "relevant"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "moment"
          }), "\n", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Reflect"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "local"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "cultural"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "nuance"
          }), " — ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "true"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "adaptation"
          }), ", ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "not"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "direct"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "translation"
          }), "\n", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Avoid"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "loanwords"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "and"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "anglicisms"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "unless"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "commonly"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "accepted"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "in"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "region"
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "Ask"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "me"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "any"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "questions"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "before"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "you"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "begin"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "that"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "will"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "help"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "you"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "get"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "best"
          }), " ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "answer"
          }), ".\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The bracketed English translations are what make the ads reviewable when you don't speak the language, so don't skip them."
      }), "\n", _jsx(_components.h3, {
        id: "review-the-translations",
        children: "Review the translations"
      }), "\n", _jsxs(_components.p, {
        children: ["For a language check, use the ", _jsx(_components.a, {
          href: "/agents/chats",
          children: "Agents chat"
        }), ": go to ", _jsx(_components.strong, {
          children: "Agents > Chats"
        }), ", paste a link to your ad, and ask about anything you're unsure of. For example: \"Is this phrase natural?\" or \"What does this say, literally?\" This is a separate agent from the one you generate ads in — it's available when your workspace has ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " enabled."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The Agents chat reviews copy but can't change it. Once you've settled on the wording you want, go back to the ad in Ad Studio and prompt the agent to make the change."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "common-questions",
        children: "Common questions"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Question"
            }), _jsx(_components.th, {
              children: "Answer"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Can it resize my ads into other formats — 9:16, 4:5, banners?"
            }), _jsx(_components.td, {
              children: "Yes. Ask for the sizes you need. If your placement has safe-zone requirements, state them explicitly (\"keep all logos and copy inside the platform safe zone\") and restate them each time you iterate. For motion ads, ask for size variants in the chat."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Will it use my product images exactly as-is, or will AI alter them?"
            }), _jsx(_components.td, {
              children: "Tell it explicitly: \"use the product photos as-is — do not alter or regenerate them.\" Repeat that instruction on every iteration."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Can it create video? What should I expect?"
            }), _jsxs(_components.td, {
              children: ["Yes. Static ads are the most mature format. Motion ads need more prompt detail, especially for animation and timing. Start from ", _jsx(_components.a, {
                href: "#create-motion-ads",
                children: "Create motion ads"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Can I add voiceover, music, or captions?"
            }), _jsx(_components.td, {
              children: "Yes. Ask for them in your prompt. Captions come word-synced to the audio."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Can I turn one long video into multiple ads?"
            }), _jsxs(_components.td, {
              children: ["Yes. Attach the video and ask for standalone clips cut on scene breaks, each with its own end card. See ", _jsx(_components.a, {
                href: "#turn-a-long-video-into-multiple-ads",
                children: "Turn a long video into multiple ads"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Why is my batch taking a while?"
            }), _jsx(_components.td, {
              children: "A batch of ads takes several minutes, and video takes longer. Start your next chat in parallel while it works."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "It redrew my stylized type treatment. Can I stop that?"
            }), _jsx(_components.td, {
              children: "Add \"don't decompose into editable text\" to your prompt when you want a type treatment left exactly as designed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "The fonts or colors came out wrong."
            }), _jsx(_components.td, {
              children: "Name them explicitly: the exact font name and the exact color, using a hex code. If a font keeps slipping, ask for the type to be overlaid rather than generated."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Am I giving it too much detail? Too little?"
            }), _jsx(_components.td, {
              children: "Static ads need less prompt detail than motion ads. Start from a template on this page, then iterate by describing one change at a time."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Do I have to re-prompt for every small tweak?"
            }), _jsxs(_components.td, {
              children: ["No. Select multiple ads and describe one change to apply across all of them, or make final tweaks in the ", _jsx(_components.a, {
                href: "/ad-studio/refine#edit-with-the-visual-editor",
                children: "visual editor"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "whats-next",
        children: "What's next"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/ads",
            children: "Create ads: Start a chat and generate concepts"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/context-hub/creative#save-visual-styles",
            children: "Styles: Define reusable visual guidelines for ad generation"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/context-hub/assets",
            children: "Assets: Upload and sync the creative files Ad Studio draws from"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/context-hub/brands",
            children: "Brand setup: Configure brand profiles, guidelines, logos, and fonts"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
